/**
 * PlayerCard — a shareable, read-only snapshot of another player.
 *
 * Opens when the URL contains `?player=<id>`. Any component (chat rows,
 * leaderboard rows, friends list, world map hover, ...) can just push
 * `?player=<id>` onto the current URL to surface the card — that URL is
 * itself the share link. Close returns to the previous state.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Copy, Check, Shield, Swords, Trophy, Sparkles, Building2, Heart,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { flagUrl } from "@/lib/utils";
import { SKILL_LABEL } from "@/lib/gameData";

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/);
  const a = (parts[0] || "?").slice(0, 1);
  const b = (parts[1] || parts[0]?.slice(1, 2) || "").slice(0, 1);
  return (a + b).toUpperCase();
}

// Deterministic warm hue per name so avatars feel differentiated without
// needing generated art. Hue is bounded to amber/rust range that matches
// the app palette.
function avatarHue(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return 18 + (h % 32); // 18..49° = warm amber → rust
}

function Avatar({ name }) {
  const hue = avatarHue(name);
  return (
    <div
      className="w-16 h-16 rounded-lg border-2 border-amber-700 flex items-center justify-center font-display text-2xl text-amber-200 shadow-inner"
      style={{ background: `linear-gradient(140deg, hsl(${hue} 55% 22%), hsl(${hue - 12} 40% 10%))` }}
      data-testid="player-card-avatar"
    >
      {initialsOf(name)}
    </div>
  );
}

function Chip({ icon: Icon, label, value, testid }) {
  return (
    <div className="panel-inset px-3 py-2 flex items-center gap-2" data-testid={testid}>
      <Icon size={14} className="text-amber-500/80" strokeWidth={1.6} />
      <span className="text-[10px] font-numeric uppercase tracking-widest text-stone-500 flex-1">{label}</span>
      <span className="font-numeric text-sm text-amber-300">{value}</span>
    </div>
  );
}

export function PlayerCard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const playerId = searchParams.get("player");
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!playerId) { setPlayer(null); setError(null); return; }
    setLoading(true);
    setError(null);
    api.get(`/api/game/social/player/${playerId}`)
      .then(({ data }) => { if (!cancelled) setPlayer(data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [playerId]);

  const close = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("player");
    setSearchParams(next, { replace: false });
  };

  const shareLink = useMemo(() => {
    if (!playerId) return "";
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    url.searchParams.set("player", playerId);
    return url.toString();
  }, [playerId]);

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Profile link copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — you can select the URL manually");
    }
  };

  if (!playerId) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
      data-testid="player-card-overlay"
    >
      <div
        className="panel w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto scroll-thin"
        onClick={(e) => e.stopPropagation()}
        data-testid="player-card"
      >
        {/* Header band */}
        <div
          className="relative px-5 pt-5 pb-4"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(12,10,9,0.6), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <button
            onClick={close}
            className="absolute top-3 right-3 text-stone-400 hover:text-amber-300 transition-colors"
            aria-label="Close"
            data-testid="player-card-close"
          >
            <X size={20} />
          </button>

          {loading && (
            <div className="text-stone-400 italic py-6 text-center font-body">Fetching the wanderer&apos;s ledger…</div>
          )}
          {error && !loading && (
            <div className="text-red-400 italic py-6 text-center font-body">{error}</div>
          )}

          {player && !loading && !error && (
            <div className="flex items-start gap-4">
              <Avatar name={player.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2
                    className="font-display text-2xl heading-gilded truncate"
                    data-testid="player-card-name"
                  >
                    {player.name}
                  </h2>
                  {player.home_country_iso_a2 && (
                    <span
                      className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full bg-stone-950/70 border border-amber-800/60"
                      title={player.home_country_name || player.home_country_iso}
                      data-testid="player-card-flag"
                    >
                      <img
                        src={flagUrl(player.home_country_iso_a2, 40)}
                        alt={player.home_country_iso || player.home_country_iso_a2}
                        loading="lazy"
                        className="w-5 h-3.5 object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.6)]"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <span className="text-[10px] font-numeric uppercase tracking-widest text-amber-200">
                        {player.home_country_iso}
                      </span>
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs font-numeric text-stone-300">
                  <span>Combat Lv. <span className="text-amber-300">{player.combat_level}</span></span>
                  <span>Total Lv. <span className="text-amber-300">{player.total_level}</span></span>
                  {player.clan_tag && (
                    <span>Clan · <span className="text-amber-300">[{player.clan_tag}]</span></span>
                  )}
                </div>
                <div className="mt-1 text-[10px] font-numeric uppercase tracking-widest">
                  <span className={
                    player.status === "In combat" ? "text-red-400"
                      : player.status === "Skilling" ? "text-emerald-400"
                      : "text-stone-500"
                  }>
                    ● {player.status}
                  </span>
                  {player.home_country_name && (
                    <span className="text-stone-500 normal-case tracking-normal ml-3 font-body">
                      Homeland · {player.home_country_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {player && !loading && !error && (
          <>
            {/* Bio */}
            <div className="px-5 py-3 border-t border-stone-800">
              <p className="text-sm text-stone-300 font-body italic" data-testid="player-card-bio">
                {player.bio}
              </p>
            </div>

            {/* Chips */}
            <div className="px-5 pb-3 grid grid-cols-2 gap-2">
              <Chip icon={Swords}     label="Combat"       value={player.combat_level} testid="pc-chip-combat" />
              <Chip icon={Shield}     label="Total Level"  value={player.total_level}  testid="pc-chip-total" />
              <Chip icon={Trophy}     label="Achievements" value={player.achievements_unlocked} testid="pc-chip-achv" />
              <Chip icon={Building2}  label="Museum"       value={player.museum_count} testid="pc-chip-museum" />
            </div>

            {/* Top skills */}
            <div className="px-5 pb-4">
              <div className="text-[10px] font-numeric uppercase tracking-widest text-amber-500 mb-1.5">
                Top skills
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {player.top_skills.map((s) => (
                  <div
                    key={s.skill}
                    className="panel-inset px-2 py-1.5 flex items-center justify-between"
                    data-testid={`pc-top-${s.skill}`}
                  >
                    <span className="text-xs font-body text-stone-300 truncate">{SKILL_LABEL[s.skill] || s.skill}</span>
                    <span className="text-[11px] font-numeric text-amber-300 ml-2">Lv. {s.level}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Share */}
            <div className="px-5 pb-5 pt-3 border-t border-stone-800 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-numeric uppercase tracking-widest text-stone-500">Shareable link</div>
                <div className="text-[11px] font-numeric text-stone-400 truncate">{shareLink}</div>
              </div>
              <button
                onClick={copyShare}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors"
                data-testid="player-card-share"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Hook: returns `openPlayer(id)` which pushes `?player=<id>` onto the URL.
 * Every entry point (chat username, leaderboard row, friends list) uses
 * this so the URL itself is the share link.
 */
export function useOpenPlayer() {
  const [searchParams, setSearchParams] = useSearchParams();
  return (id) => {
    if (!id) return;
    const next = new URLSearchParams(searchParams);
    next.set("player", id);
    setSearchParams(next, { replace: false });
  };
}
